import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { Modal } from "../ui/modal";
import { CountryPicker, CurrencyPicker, TimezonePicker } from "../ui/pickers";
import styles from "./operator-page.module.css";
import type { MailKind, MailUsage } from "@dentacore/shared-types";

import {
  addressInUse,
  createClinic,
  deactivateOperator,
  listClinics,
  listOperators,
  mailUsage,
  platformHealth,
  restore,
  signIn,
  signOut,
  storedToken,
} from "./operator-api";
import { AccountingSection } from "./accounting-section";
import { HelpQuestionsSection } from "./help-questions-section";
import { ClinicDetailPage, ClinicStatus } from "./clinic-detail";
import { PricesSection } from "./prices-section";
import type {
  ClinicSummary,
  CreatedClinic,
  NewClinic,
  OperatorSession,
  OperatorSummary,
  PlatformHealth,
} from "./operator-api";
import { RequiredMark } from "../ui/required-mark";

/**
 * The operator console (STORY-063, decisions O1–O3).
 *
 * **Outside the clinic shell on purpose.** No sidebar, no branch switcher, no
 * clinic branding — this is not a screen inside somebody's practice, and
 * dressing it as one would invite exactly the confusion that matters most here:
 * *whose data am I looking at.* The page says the platform's name and nothing
 * else's.
 *
 * It renders its own sign-in rather than sharing the clinic's, because the two
 * sessions are separate all the way down: different tokens, different secrets,
 * different storage.
 */
export function OperatorPage(): React.JSX.Element {
  const [session, setSession] = useState<OperatorSession | null>(null);
  const [checking, setChecking] = useState(storedToken() !== null);

  useEffect(() => {
    if (!checking) return;
    let live = true;
    void restore().then((resumed) => {
      if (!live) return;
      setSession(resumed);
      setChecking(false);
    });
    return () => {
      live = false;
    };
  }, [checking]);

  if (checking) {
    return (
      <div className={styles.shell}>
        <p className={styles.muted}>Restoring your session…</p>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      {session === null ? (
        <SignInForm onSignedIn={setSession} />
      ) : (
        <Console
          session={session}
          onSignedOut={() => {
            signOut();
            setSession(null);
          }}
        />
      )}
    </div>
  );
}

function SignInForm({
  onSignedIn,
}: {
  onSignedIn: (s: OperatorSession) => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className={styles.signIn}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        void signIn(email, password)
          .then(onSignedIn)
          .catch((cause: unknown) => {
            setError(cause instanceof Error ? cause.message : "Sign-in failed");
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h1 className={styles.title}>DentaCore operator</h1>
      <p className={styles.muted}>
        Provisioning and platform health. This is not a clinic account.
      </p>

      <label className={styles.field}>
        <span>
          Email
          <RequiredMark />
        </span>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          required
          data-testid="operator-email"
        />
      </label>

      <label className={styles.field}>
        <span>
          Password
          <RequiredMark />
        </span>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          required
          data-testid="operator-password"
        />
      </label>

      {error !== null && (
        <p className={styles.error} role="alert" data-testid="operator-error">
          {error}
        </p>
      )}

      <button type="submit" className={styles.primary} disabled={busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

/** The three things the console is about. Only two are not a clinic. */
type Section = "clinics" | "accounting" | "prices" | "operators" | "health" | "help";

/**
 * The three states a customer is actually in, as the operator thinks of them.
 *
 * Not a column on `clinics` — it is derived, because both halves already exist:
 * `isSuspended` is a real field, and `canTakeBookings` is the platform's own
 * answer to "has this practice finished setting itself up". Storing a fourth
 * status would give two sources for one fact.
 */
type ClinicLane = "active" | "pending" | "suspended";

const LANE_LABELS: Record<ClinicLane, string> = {
  active: "Active",
  pending: "Pending setup",
  suspended: "Suspended",
};

const LANE_EMPTY: Record<ClinicLane, string> = {
  active: "No clinic is taking bookings yet.",
  pending: "Nothing is waiting on setup — every clinic here can take bookings.",
  suspended: "No clinic is suspended.",
};

function Console({
  session,
  onSignedOut,
}: {
  session: OperatorSession;
  onSignedOut: () => void;
}): React.JSX.Element {
  const [section, setSection] = useState<Section>("clinics");
  const [openClinic, setOpenClinic] = useState<string | null>(null);
  const [clinics, setClinics] = useState<ClinicSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedClinic | null>(null);
  const [search, setSearch] = useState("");
  const [clinicLane, setClinicLane] = useState<ClinicLane>("active");

  const refresh = useCallback(() => {
    void listClinics()
      .then((rows) => {
        setClinics(rows);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load clinics");
      });
  }, []);

  useEffect(refresh, [refresh]);

  const term = search.trim().toLowerCase();

  /**
   * Which of the three states a clinic is in.
   *
   * The list used to be one undivided roll, so a suspended customer and one
   * that had never finished setting itself up sat between two live practices
   * and read the same. These are the three questions actually asked of this
   * screen — who is running, who is half-provisioned, who is switched off —
   * and `canTakeBookings` already told us the middle one; nothing new is
   * computed here.
   */
  const lane = (clinic: ClinicSummary): ClinicLane =>
    clinic.isSuspended ? "suspended" : clinic.canTakeBookings ? "active" : "pending";

  const matchesSearch = (clinic: ClinicSummary): boolean =>
    term === "" ||
    clinic.name.toLowerCase().includes(term) ||
    clinic.slug.toLowerCase().includes(term);

  const all = clinics ?? [];
  const counts: Record<ClinicLane, number> = {
    active: all.filter((c) => lane(c) === "active").length,
    pending: all.filter((c) => lane(c) === "pending").length,
    suspended: all.filter((c) => lane(c) === "suspended").length,
  };

  const shown = all.filter((clinic) => lane(clinic) === clinicLane && matchesSearch(clinic));

  const bookable = counts.active;

  return (
    <div className={styles.layout}>
      <nav className={styles.rail}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 3.5c-2.6 0-4.3 2.1-4.3 4.8 0 3.1 1.3 4.7 1.9 7.8.4 2.1 1 4.4 2.4 4.4 1.6 0 1.6-4.5 4-4.5s2.4 4.5 4 4.5c1.4 0 2-2.3 2.4-4.4.6-3.1 1.9-4.7 1.9-7.8 0-2.7-1.7-4.8-4.3-4.8-1.7 0-2.6.9-4 .9S9.7 3.5 8 3.5z" />
            </svg>
          </span>
          <span className={styles.brandText}>
            DentaCore
            <span>operator</span>
          </span>
        </div>
        {(
          [
            ["clinics", "Clinics", clinics?.length],
            ["accounting", "Accounting", undefined],
            ["prices", "Prices", undefined],
            ["operators", "Operators", undefined],
            ["health", "Health", undefined],
            ["help", "Help questions", undefined],
          ] as [Section, string, number | undefined][]
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            className={section === key ? styles.navOn : styles.nav}
            data-card=""
            data-testid={`nav-${key}`}
            onClick={() => {
              setSection(key);
              setOpenClinic(null);
            }}
          >
            {label}
            {count === undefined ? null : <span className={styles.navCount}>{count}</span>}
          </button>
        ))}

        <div className={styles.railFoot}>
          <span className={styles.muted}>{session.name}</span>
          <button type="button" className={styles.ghost} onClick={onSignedOut}>
            Sign out
          </button>
        </div>
      </nav>

      <main className={styles.main}>
        {section === "clinics" && openClinic !== null && (
          <ClinicDetailPage
            clinicId={openClinic}
            onBack={() => {
              setOpenClinic(null);
              refresh();
            }}
          />
        )}

        {section === "clinics" && openClinic === null && (
          <div className={styles.console}>
            <header className={styles.head}>
              <div>
                <h1 className={styles.title}>Clinics</h1>
                <p className={styles.muted}>
                  {clinics === null
                    ? "Loading…"
                    : `${String(clinics.length)} on the platform · ${String(bookable)} taking bookings`}
                </p>
              </div>
              <button
                type="button"
                className={styles.primary}
                onClick={() => {
                  setCreating(true);
                }}
                data-testid="add-clinic"
              >
                Add a clinic
              </button>
            </header>

            {created !== null && (
              <NewClinicHandover
                created={created}
                onDone={() => {
                  setCreated(null);
                }}
              />
            )}

            {error !== null && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}

            {clinics !== null && clinics.length > 0 && (
              <div className={styles.lanes} role="tablist" aria-label="Clinics by state">
                {(["active", "pending", "suspended"] as ClinicLane[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={clinicLane === key}
                    className={clinicLane === key ? styles.laneOn : styles.lane}
                    onClick={() => {
                      setClinicLane(key);
                    }}
                    data-testid={`clinic-lane-${key}`}
                  >
                    {LANE_LABELS[key]}
                    <span className={styles.laneCount}>{counts[key]}</span>
                  </button>
                ))}
              </div>
            )}

            {clinics !== null && clinics.length > 3 && (
              <input
                className={styles.search}
                placeholder="Search by name or subdomain…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            )}

            {clinics !== null && clinics.length === 0 && (
              <p className={styles.muted}>
                No clinics yet. The first one you add is the first customer on the platform.
              </p>
            )}

            {/*
              An empty *lane* is not an empty platform, and saying "no clinics"
              here would be a lie the moment one is suspended. The search term
              gets its own sentence for the same reason.
            */}
            {clinics !== null && clinics.length > 0 && shown.length === 0 && (
              <p className={styles.muted}>
                {term === ""
                  ? LANE_EMPTY[clinicLane]
                  : `No ${LANE_LABELS[clinicLane].toLowerCase()} clinic matches "${search.trim()}".`}
              </p>
            )}

            {shown.length > 0 && (
              <ul className={styles.rows} data-testid="clinic-list">
                {shown.map((clinic) => (
                  <li key={clinic.id} className={styles.row}>
                    <button
                      type="button"
                      className={styles.rowOpen}
                      data-card=""
                      data-testid="clinic-row"
                      onClick={() => {
                        setOpenClinic(clinic.id);
                      }}
                    >
                      <span className={styles.rowMain}>
                        <span className={styles.rowName}>
                          {clinic.name}
                          {clinic.isDemo && <span className={styles.demo}>demo</span>}
                        </span>
                        <span className={styles.rowSub}>
                          {clinic.slug} · {clinic.timezone} · {clinic.currency}
                        </span>
                      </span>

                      <span className={styles.counts}>
                        <Count n={clinic.branchCount} label="locations" />
                        <Count n={clinic.userCount} label="people" />
                        <Count n={clinic.patientCount} label="patients" />
                      </span>

                      <ClinicStatus clinic={clinic} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {section === "accounting" && (
          <AccountingSection
            onOpenClinic={(clinicId) => {
              setSection("clinics");
              setOpenClinic(clinicId);
            }}
          />
        )}
        {section === "prices" && <PricesSection />}
        {section === "operators" && <OperatorsSection selfEmail={session.email} />}
        {section === "health" && <HealthSection />}
        {section === "help" && <HelpQuestionsSection />}
      </main>

      {creating && (
        <NewClinicForm
          onCancel={() => {
            setCreating(false);
          }}
          onCreated={(clinic) => {
            setCreating(false);
            setCreated(clinic);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * The other operators.
 *
 * **There is no "Add operator" button, and there never will be** (decision O1).
 * No endpoint mints a platform admin — `pnpm db:create-operator` needs database
 * access, which is a far higher bar than a session. A privilege-escalation bug
 * cannot exist in a code path that does not exist.
 */
function OperatorsSection({ selfEmail }: { selfEmail: string }): React.JSX.Element {
  const [operators, setOperators] = useState<OperatorSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void listOperators()
      .then((rows) => {
        setOperators(rows);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load operators");
      });
  }, []);

  useEffect(load, [load]);

  return (
    <div className={styles.console}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Operators</h1>
          <p className={styles.muted}>
            Accounts that can reach every clinic. New ones are created with{" "}
            <code>pnpm db:create-operator</code> — deliberately not from here.
          </p>
        </div>
      </header>

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {operators !== null && (
        <ul className={styles.rows} data-testid="operator-list">
          {operators.map((operator) => (
            <li key={operator.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowName}>
                  {operator.name}
                  {operator.email === selfEmail && <span className={styles.demo}>you</span>}
                </span>
                <span className={styles.rowSub}>{operator.email}</span>
              </div>

              <span className={styles.counts}>
                <Count n={operator.clinicsProvisioned} label="provisioned" />
                <span className={styles.count}>
                  {operator.lastLoginAt === null
                    ? "never signed in"
                    : `last seen ${new Date(operator.lastLoginAt).toLocaleDateString()}`}
                </span>
              </span>

              {operator.isActive ? (
                <button
                  type="button"
                  className={styles.ghost}
                  disabled={operator.email === selfEmail}
                  data-testid="deactivate-operator"
                  onClick={() => {
                    void deactivateOperator(operator.id)
                      .then(load)
                      .catch((cause: unknown) => {
                        setError(cause instanceof Error ? cause.message : "Could not deactivate");
                      });
                  }}
                >
                  Deactivate
                </button>
              ) : (
                <span className={styles.chipOff}>Deactivated</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** What the platform can actually report on — no invented metrics. */
function HealthSection(): React.JSX.Element {
  const [health, setHealth] = useState<PlatformHealth | null>(null);
  const [mail, setMail] = useState<MailUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void platformHealth()
      .then(setHealth)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not read platform health");
      });

    // **Its own request, and its own failure.** A relay counter that cannot be
    // read is no reason to hide the worker queue — the two answer different
    // questions and one being unavailable should not blank the other.
    void mailUsage()
      .then(setMail)
      .catch(() => {
        setMail(null);
      });
  }, []);

  const age = (seconds: number | null): string => {
    if (seconds === null) return "nothing waiting";
    if (seconds < 90) return `${String(seconds)}s behind`;
    if (seconds < 5400) return `${String(Math.round(seconds / 60))} min behind`;
    return `${String(Math.round(seconds / 3600))} h behind`;
  };

  return (
    <div className={styles.console}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Health</h1>
          <p className={styles.muted}>
            The background worker&rsquo;s queue, and the platform total.
          </p>
        </div>
      </header>

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {health !== null && (
        <div className={styles.figures} data-testid="health-figures">
          <Figure
            label="Jobs waiting"
            value={health.outboxPending}
            note={age(health.oldestPendingAgeSeconds)}
            // A backlog is only worrying if it is *old*. A thousand events one
            // second behind is a busy minute; one event an hour behind is a
            // worker that has stopped.
            tone={
              health.oldestPendingAgeSeconds !== null && health.oldestPendingAgeSeconds > 900
                ? "bad"
                : "plain"
            }
          />
          <Figure
            label="Dead-lettered"
            value={health.outboxDeadLettered}
            note={
              health.outboxDeadLettered === 0 ? "nothing given up on" : "raised as clinic alerts"
            }
            tone={health.outboxDeadLettered > 0 ? "bad" : "good"}
          />
          <Figure label="Clinics" value={health.clinics} note="on the platform" tone="plain" />
          <Figure
            label="Suspended"
            value={health.suspendedClinics}
            note={health.suspendedClinics === 0 ? "none" : "cannot sign in"}
            tone={health.suspendedClinics > 0 ? "warn" : "plain"}
          />
        </div>
      )}

      {mail !== null && <MailPanel usage={mail} />}
    </div>
  );
}

/** What a kind is called on screen. */
const MAIL_KIND_LABELS: Record<MailKind, string> = {
  STAFF_INVITE: "Staff invitations",
  CLINIC_INVITE: "Clinic invitations",
  PRACTICE_ADDED: "Practice-added notices",
  PASSWORD_RESET: "Password resets",
  SIGNUP_WELCOME: "Sign-up welcomes",
};

/**
 * Mail, as a gauge — 2026-09-20.
 *
 * *"can we please add a counter so i know how many emails ive sent today, and
 * from what account, i would like to be able to manage this"*.
 *
 * **It never blocks anything, and the copy has to say so**, because a bar that
 * fills up reads like a limit being enforced. A hard cap was offered and
 * declined the same day: the one message with no on-screen fallback is a
 * password reset, so refusing sends to save quota would lock people out of the
 * product.
 */
function MailPanel({ usage }: { usage: MailUsage }): React.JSX.Element {
  const attempted = usage.today.sent + usage.today.failed;
  const limit = usage.dailyLimit;

  // Amber from four fifths. Far enough along to be worth knowing, late enough
  // that an ordinary morning does not sit permanently at a warning.
  const fraction = limit === null ? null : Math.min(1, attempted / limit);
  const tone = fraction === null ? "plain" : fraction >= 0.8 ? "warn" : "plain";

  return (
    <section className={styles.mailPanel} data-testid="mail-usage">
      <header className={styles.mailHead}>
        <h2 className={styles.h2}>Mail</h2>
        <p className={styles.muted}>
          {usage.senders.length === 0
            ? "Nothing sent today."
            : `Sent as ${usage.senders.join(", ")}.`}{" "}
          Counted here as each message leaves, so it follows the account rather than the provider.
        </p>
      </header>

      <div className={styles.figures}>
        <Figure
          label="Today"
          value={usage.today.sent}
          note={limit === null ? "sent" : `of ${String(limit)} believed available`}
          tone={tone}
        />
        <Figure
          label="Failed today"
          value={usage.today.failed}
          note={usage.today.failed === 0 ? "none refused" : "refused by the relay"}
          tone={usage.today.failed > 0 ? "bad" : "good"}
        />
        <Figure label="Last 7 days" value={usage.last7Days.sent} note="sent" tone="plain" />
      </div>

      {fraction !== null && (
        <div className={styles.mailBar} data-testid="mail-bar">
          <div
            className={tone === "warn" ? styles.mailBarFillWarn : styles.mailBarFill}
            style={{ width: `${String(Math.round(fraction * 100))}%` }}
          />
        </div>
      )}

      <p className={styles.hint}>
        Advisory only &mdash; nothing is ever blocked. Every screen that sends an invitation also
        shows the link, so a failed send is recoverable by hand. A password reset is the one that is
        not.
      </p>

      {usage.byKind.length > 0 && (
        <table className={styles.mailTable} data-testid="mail-by-kind">
          <tbody>
            {usage.byKind.map((row) => (
              <tr key={row.kind}>
                <td>{MAIL_KIND_LABELS[row.kind]}</td>
                <td className={styles.mailCount}>{row.sent}</td>
                <td className={styles.mailCount}>
                  {row.failed === 0 ? "" : `${String(row.failed)} failed`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {usage.byClinic.length > 0 && (
        <>
          <h3 className={styles.mailSub}>By practice, today</h3>
          <table className={styles.mailTable} data-testid="mail-by-clinic">
            <tbody>
              {usage.byClinic.map((row) => (
                <tr key={row.clinicId}>
                  <td>
                    {row.name} <span className={styles.hint}>{row.slug}</span>
                  </td>
                  <td className={styles.mailCount}>{row.sent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {usage.recentFailures.length > 0 && (
        <>
          {/*
            The relay's own words. "68 sent, 12 failed" does not say whether the
            allowance ran out, a credential expired or a recipient was refused —
            and that is the only question worth asking on this screen.
          */}
          <h3 className={styles.mailSub}>Recent failures</h3>
          <ul className={styles.mailFailures} data-testid="mail-failures">
            {usage.recentFailures.map((row) => (
              <li key={`${row.at}-${row.kind}`}>
                <span className={styles.hint}>
                  {new Date(row.at).toLocaleString()} &middot; {MAIL_KIND_LABELS[row.kind]}
                </span>
                <br />
                {row.failure}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const Figure = ({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: number;
  note: string;
  tone: "plain" | "good" | "warn" | "bad";
}): React.JSX.Element => (
  <div className={styles.figure}>
    <span className={styles.figureLabel}>{label}</span>
    <strong
      className={
        tone === "bad"
          ? styles.figureBad
          : tone === "warn"
            ? styles.figureWarn
            : tone === "good"
              ? styles.figureGood
              : styles.figureValue
      }
    >
      {value}
    </strong>
    <span className={styles.figureNote}>{note}</span>
  </div>
);

const BLANK: NewClinic = {
  name: "",
  slug: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  currency: "",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  temporaryPassword: "",
};

function NewClinicForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (clinic: CreatedClinic) => void;
}): React.JSX.Element {
  const [form, setForm] = useState<NewClinic>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  /**
   * Whether the address typed above already has a login (I2, 2026-09-20).
   *
   * **Three states, and `null` is a real one.** True hides the password field
   * and explains why; false draws it; `null` means *not asked yet* — the field
   * is blank, half typed, or the lookup is in flight. Drawing the field while
   * `null` is the right default: it is what the form has always done, and
   * flickering it away a moment after somebody starts typing into it would be
   * worse than the server's refusal.
   */
  const [addressKnown, setAddressKnown] = useState<boolean | null>(null);

  const set = <K extends keyof NewClinic>(key: K, value: NewClinic[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Ask, once the typing settles.
   *
   * **Debounced because it fires per keystroke otherwise**, and this is an
   * address-shaped lookup on the owner connection — cheap, but not free, and
   * there is no reason to run it against `a`, `an`, `ant`, `anto`. 400ms is the
   * usual "they have stopped typing" threshold.
   *
   * **Aborted on the way out**, so a slow answer for an address that has since
   * been edited cannot land on a newer one. Without it, backspacing from a
   * known address to a new one could leave the field hidden.
   *
   * A failed lookup falls back to `null` — draw the field — rather than to
   * false. The server refuses a password for a known address regardless, so the
   * worst case is the refusal the operator would have got anyway, not a second
   * identity.
   */
  const email = form.adminEmail.trim().toLowerCase();

  useEffect(() => {
    if (!email.includes("@") || email.endsWith("@")) {
      setAddressKnown(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      addressInUse(email, controller.signal)
        .then((known) => {
          setAddressKnown(known);
        })
        .catch(() => {
          setAddressKnown(null);
        });
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [email]);

  return (
    <Modal label="Add a clinic" onClose={onCancel} panelClassName={styles.dialog}>
      <form
        className={styles.dialogBody}
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void createClinic({
            ...form,
            currency: form.currency.toUpperCase(),
            defaultCountry: form.defaultCountry?.toUpperCase() ?? undefined,
            // **Omitted, not empty.** `@IsOptional()` skips `undefined`, not
            // `""` — an empty string reaches the twelve-character minimum and
            // is refused, so leaving the field blank would fail rather than
            // fall back to the invite flow.
            temporaryPassword:
              form.temporaryPassword?.trim() === "" ? undefined : form.temporaryPassword?.trim(),
          })
            .then(onCreated)
            .catch((cause: unknown) => {
              setError(cause instanceof Error ? cause.message : "Could not create the clinic");
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <h2 className={styles.h2}>Add a clinic</h2>
        <p className={styles.muted}>
          This creates the practice and its first admin. Everything else — locations, chairs, hours,
          people, procedures — they set up themselves, or you can do it for them afterwards.
        </p>

        <label className={styles.field}>
          <span>
            Clinic name
            <RequiredMark />
          </span>
          <input
            value={form.name}
            required
            data-testid="clinic-name"
            onChange={(e) => {
              set("name", e.target.value);
              // A slug suggested from the name until somebody edits it, then
              // left alone — a field that keeps overwriting what you typed is
              // worse than one that never helps.
              if (!slugTouched) {
                set(
                  "slug",
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, ""),
                );
              }
            }}
          />
        </label>

        <label className={styles.field}>
          <span>
            Subdomain
            <RequiredMark />
          </span>
          <input
            value={form.slug}
            required
            data-testid="clinic-slug"
            onChange={(e) => {
              setSlugTouched(true);
              set("slug", e.target.value);
            }}
          />
          <small className={styles.hint}>
            Where they reach DentaCore. Lower-case letters, digits and hyphens.
          </small>
        </label>

        {/*
          **The same three pickers the clinic's own settings use.**

          These stayed free-text boxes here long after Settings stopped having
          them, and the country one asked the operator to know that Lebanon is
          `LB` — a two-letter code typed from memory, validated strictly, with
          the list nowhere on screen. Provisioning a customer is the *first*
          thing anybody does, so it was the worst place to keep the version that
          made you guess.
        */}
        <div className={styles.pair}>
          <TimezonePicker
            id="clinic-timezone"
            label="Timezone"
            required
            value={form.timezone}
            onChange={(next) => {
              set("timezone", next);
            }}
            testId="clinic-timezone"
          />

          <CurrencyPicker
            id="clinic-currency"
            label="Currency"
            required
            value={form.currency}
            onChange={(next) => {
              set("currency", next);
            }}
            testId="clinic-currency"
            note="Hard to change once a price exists."
          />
        </div>

        <CountryPicker
          id="clinic-country"
          label="Country"
          value={form.defaultCountry ?? ""}
          onChange={(next) => {
            set("defaultCountry", next);
          }}
          testId="clinic-country"
          note="What a phone number typed without a country code is read as."
        />

        <h3 className={styles.h3}>Their first admin</h3>

        <div className={styles.pair}>
          <label className={styles.field}>
            <span>
              First name
              <RequiredMark />
            </span>
            <input
              value={form.adminFirstName}
              required
              data-testid="admin-first-name"
              onChange={(e) => {
                set("adminFirstName", e.target.value);
              }}
            />
          </label>

          <label className={styles.field}>
            <span>
              Last name
              <RequiredMark />
            </span>
            <input
              value={form.adminLastName}
              required
              data-testid="admin-last-name"
              onChange={(e) => {
                set("adminLastName", e.target.value);
              }}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span>
            Email
            <RequiredMark />
          </span>
          <input
            type="email"
            value={form.adminEmail}
            required
            data-testid="admin-email"
            onChange={(e) => {
              set("adminEmail", e.target.value);
            }}
          />
          <small className={styles.hint}>Their sign-in address.</small>
        </label>

        {/*
          **An address that already has a login cannot be given a password here**
          (I2, 2026-09-20).

          Provisioning used to mint a fresh identity every time, so naming
          somebody who already worked at another practice gave one human two
          passwords — they could sign in to the older clinic and not the newer
          one. It now attaches to the identity they have, which means there is
          no password for this form to set: they already have one.

          Said before the operator types rather than refused after they submit,
          because they are usually on the phone with the customer and "the
          password you just agreed is not going to be used" is a worse sentence
          than never offering the field.
        */}
        {addressKnown === true ? (
          <p className={styles.knownAddress} data-testid="admin-email-known">
            <strong>This address already has a DentaCore account.</strong> No password is set here
            &mdash; this practice is added to the account they have, and they sign in to it with the
            login they already use. Their other practices are not affected.
          </p>
        ) : (
          /*
            **The one-time password** (decision SA1, 2026-09-06).

            Leave it blank and the old flow runs: an account they cannot use
            until they walk a single-use link mailed to them. Fill it in and the
            account works immediately with this password, and they are made to
            replace it the first time they sign in.

            A text input rather than `type="password"` on purpose — the operator
            is about to read this out or type it into a message, and hiding it
            from the person who just chose it protects nobody.
          */
          <label className={styles.field}>
            <span>One-time password</span>
            <input
              type="text"
              value={form.temporaryPassword}
              autoComplete="off"
              spellCheck={false}
              data-testid="admin-temp-password"
              onChange={(e) => {
                set("temporaryPassword", e.target.value);
              }}
            />
            <small className={styles.hint}>
              Set one and tell them &mdash; they must replace it on first sign-in, and no email is
              sent. Leave blank to mail a single-use link instead. Minimum 12 characters.
            </small>
          </label>
        )}

        {error !== null && (
          <p className={styles.error} role="alert" data-testid="create-error">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy}
            data-testid="create-clinic"
          >
            {busy ? "Creating…" : "Create clinic"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const Count = ({ n, label }: { n: number; label: string }): React.JSX.Element => (
  <span className={styles.count}>
    <strong>{n}</strong> {label}
  </span>
);

/**
 * The invite link for a clinic that has just been created, shown once.
 *
 * **This is the only time the token is readable.** Only its SHA-256 reaches the
 * database, so no screen can show it again — which is why the panel is loud and
 * has to be dismissed deliberately.
 *
 * It appears whether or not the email left. If it did, this is a belt-and-braces
 * copy; if it did not, it is the whole fallback, and saying so is the difference
 * between a customer waiting a day and a customer waiting a minute.
 */
function NewClinicHandover({
  created,
  onDone,
}: {
  created: CreatedClinic;
  onDone: () => void;
}): React.JSX.Element {
  const link = created.inviteUrl;
  const [copied, setCopied] = useState(false);

  /**
   * **A password was issued, so there is no link and nothing was emailed.**
   *
   * The panel says what the operator has to do next — read out an address and a
   * password they already know — rather than offering a link that does not
   * exist. It deliberately does not repeat the password back: the operator
   * typed it seconds ago, and putting a live credential on a screen that stays
   * open is how it ends up in a screenshot.
   */
  if (created.issuedPassword) {
    return (
      <section className={styles.handover} data-testid="invite-handover">
        <h2 className={styles.h2}>{created.name} is ready</h2>
        <p>
          Tell <strong>{created.adminEmail}</strong> the password you just set. They sign in with
          it, are asked to choose their own, and are the clinic&rsquo;s first admin from that
          moment.
        </p>

        <div className={styles.linkRow}>
          <code className={styles.link} data-testid="signin-url">
            {created.signInUrl}
          </code>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => {
              void navigator.clipboard?.writeText(created.signInUrl).then(() => {
                setCopied(true);
              });
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <p className={styles.muted}>
          No email was sent and no invite link exists &mdash; a live link beside a live password
          would be a second way in that nobody is tracking.
        </p>

        <button type="button" className={styles.primary} onClick={onDone}>
          Done
        </button>
      </section>
    );
  }

  /**
   * **They already had an account, so the practice is already theirs** (OA1,
   * 2026-09-20).
   *
   * The same shape as the one-time-password panel above and for the same
   * reason: there is no link, because there is nothing to accept. This used to
   * be the invite panel with a softer sentence, and the sentence was the
   * problem — the operator handed over a link, the customer clicked it to
   * "join" a clinic they had just paid for, and until they did the practice was
   * invisible at sign-in. That was reported as a bug twice before it was one.
   *
   * **The mail failing is worth saying and is not a blocker here**, unlike the
   * invite case where a lost mail means a lost link. Nothing is lost: the
   * practice works, and the operator can read the address out.
   */
  if (created.joinedExistingAccount) {
    return (
      <section
        className={created.emailed ? styles.handover : styles.handoverWarn}
        data-testid="invite-handover"
      >
        <h2 className={styles.h2}>{created.name} is ready</h2>
        <p>
          <strong>{created.adminEmail}</strong> already has a DentaCore account, so this practice
          has been added to it and they are its admin. They sign in with the password they already
          use &mdash; there is nothing to accept and their other practices are untouched.
        </p>

        <div className={styles.linkRow}>
          <code className={styles.link} data-testid="signin-url">
            {created.signInUrl}
          </code>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => {
              void navigator.clipboard?.writeText(created.signInUrl).then(() => {
                setCopied(true);
              });
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <p className={styles.muted}>
          {created.emailed
            ? "We emailed them to say so. No invite link exists — they are already in."
            : "The email did not go, which changes nothing about the practice — send them this address yourself."}
        </p>

        <button type="button" className={styles.primary} onClick={onDone}>
          Done
        </button>
      </section>
    );
  }

  return (
    <section
      className={created.emailed ? styles.handover : styles.handoverWarn}
      data-testid="invite-handover"
    >
      <h2 className={styles.h2}>
        {created.emailed
          ? `${created.name} is ready`
          : `${created.name} created — email did not go`}
      </h2>
      <p>
        {/*
          **One case reaches here now** (OA1, 2026-09-20): a brand-new address
          with no one-time password, which is the only kind of admin left who
          has to walk a link before they have anything to sign in with.
          Somebody who already had an account used to be sent here too, with a
          second sentence explaining that they would not be choosing a password;
          they are active immediately instead, and the panel above says so.
        */}
        {created.emailed ? (
          <>
            We emailed <strong>{created.adminEmail}</strong>. They set their own password and become
            the clinic&rsquo;s first admin &mdash; no password travels from here.
          </>
        ) : (
          <>
            <strong>The invite could not be sent.</strong> The clinic and the link are both fine —
            send this to <strong>{created.adminEmail}</strong> yourself, or reissue once mail is
            working.
          </>
        )}
      </p>

      <div className={styles.linkRow}>
        <code className={styles.link} data-testid="invite-link">
          {link}
        </code>
        <button
          type="button"
          className={styles.ghost}
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <p className={styles.muted}>
        They sign in at <strong>{created.slug}</strong>. The link expires{" "}
        {new Date(created.inviteExpiresAt).toLocaleDateString()} and works once.
      </p>

      <button type="button" className={styles.primary} onClick={onDone}>
        Done
      </button>
    </section>
  );
}
